import { Grid, TextField, Typography } from '@material-ui/core'
import React from 'react'

import SectionTitle from '../../../components/SectionTitle'
import { convertPersianNumbersToEnglishNumbers } from '../../../helpers/numbers'
import { getTranslate } from '../../../localization'
import DateRangeList from '../components/DateRangeList'
import DaysOfWeekPicker from '../components/DaysOfWeekPicker'
import { DateRangeValue } from '../types'
import useStyles from './styles/section.styles'

interface ScheduleSectionProps {
  stayRanges: DateRangeValue[]
  bookingRanges: DateRangeValue[]
  blackoutRanges: DateRangeValue[]
  daysOfWeek: number[]
  advancePurchaseMinDays: string
  advancePurchaseMaxDays: string
  onRangesChange: (
    key: 'stayRanges' | 'bookingRanges' | 'blackoutRanges',
    ranges: DateRangeValue[]
  ) => void
  onDaysChange: (days: number[]) => void
  onNumberChange: (
    key: 'advancePurchaseMinDays' | 'advancePurchaseMaxDays',
    value: string
  ) => void
  fieldErrors: Record<string, string>
}

/**
 * When the promotion applies.
 *
 * TWO windows, deliberately separate. The stay window is which nights are discounted; the
 * booking window is when the booking may be made. The form previously carried one field
 * labelled "Start date — End date" that wrote the stay window and said nothing about which
 * of the two it was — and on the demo data the two are identical, which hid the
 * conflation rather than exposing it.
 *
 * All three lists are repeatable, because a campaign that runs over two holidays is two
 * ranges, not one long one that also discounts the gap between them.
 */
const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  stayRanges,
  bookingRanges,
  blackoutRanges,
  daysOfWeek,
  advancePurchaseMinDays,
  advancePurchaseMaxDays,
  onRangesChange,
  onDaysChange,
  onNumberChange,
  fieldErrors
}) => {
  const classes = useStyles()

  return (
    <div className={classes.section}>
      <SectionTitle title="When it applies" marginBottom />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DateRangeList
            label="Stay window — which nights are discounted"
            helperText="The nights the discount applies to. Add a period for each separate run."
            addLabel="Add stay period"
            removeLabel="Remove stay period"
            name="stayRanges"
            ranges={stayRanges}
            onChange={(ranges) => onRangesChange('stayRanges', ranges)}
            error={fieldErrors.stayRanges}
          />
        </Grid>
        <Grid item xs={12}>
          <DateRangeList
            label="Booking window — when the booking may be made"
            helperText="Leave empty to accept bookings at any time."
            addLabel="Add booking period"
            removeLabel="Remove booking period"
            name="bookingRanges"
            ranges={bookingRanges}
            onChange={(ranges) => onRangesChange('bookingRanges', ranges)}
            error={fieldErrors.bookingRanges}
            startEmpty
          />
        </Grid>
        <Grid item xs={12}>
          <DaysOfWeekPicker value={daysOfWeek} onChange={onDaysChange} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="text"
            inputMode="numeric"
            id="advancePurchaseMinDays"
            name="advancePurchaseMinDays"
            label={getTranslate('Booked at least this many days ahead')}
            value={advancePurchaseMinDays}
            error={Boolean(fieldErrors.advancePurchaseMinDays)}
            helperText={
              fieldErrors.advancePurchaseMinDays ||
              getTranslate('Leave empty for no minimum.')
            }
            onChange={(event) =>
              onNumberChange(
                'advancePurchaseMinDays',
                convertPersianNumbersToEnglishNumbers(event.target.value)
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="text"
            inputMode="numeric"
            id="advancePurchaseMaxDays"
            name="advancePurchaseMaxDays"
            label={getTranslate('Booked at most this many days ahead')}
            value={advancePurchaseMaxDays}
            error={Boolean(fieldErrors.advancePurchaseMaxDays)}
            helperText={
              fieldErrors.advancePurchaseMaxDays ||
              getTranslate('Leave empty for no maximum.')
            }
            onChange={(event) =>
              onNumberChange(
                'advancePurchaseMaxDays',
                convertPersianNumbersToEnglishNumbers(event.target.value)
              )
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DateRangeList
            label="Blackout dates"
            helperText="Nights excluded from the promotion even when they fall inside the stay window."
            addLabel="Add blackout period"
            removeLabel="Remove blackout period"
            name="blackoutRanges"
            ranges={blackoutRanges}
            onChange={(ranges) => onRangesChange('blackoutRanges', ranges)}
            error={fieldErrors.blackoutRanges}
            startEmpty
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" className={classes.note}>
            {getTranslate(
              'A night must fall inside the stay window, on a selected day of week, and outside every blackout period.'
            )}
          </Typography>
        </Grid>
      </Grid>
    </div>
  )
}

export default ScheduleSection
